
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Button, ButtonGroup, Card, CardBody, Container, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import Flatpickr from "react-flatpickr";
import { mergeOperationalDateCaps } from "../../../utils/flatpickrOperational";
import { useRecoilValue } from "recoil";
import { themeTypeState } from "../../../state/GlobalState";
import { exportFlightRequest, flightListRequest, mailFlightRequest, viewFlightRequest } from "../../../api/controller/CaseReportController";
import { getArrivalFlightListRequest, getDepartureFlightListRequest } from "../../../api/controller/CaseFileController";
import { handleResponse } from "../../../custom/helpers/HandleResponse";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CASE_FILE_TYPE_PILL, caseFileTypePillBase } from '../caseFiles/state';

const TYPE_PILL_DEFAULT = { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' };

const TypeCounts = ({ counts }) => {
    if (!counts || !Object.keys(counts).length) return null;
    return (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {Object.entries(counts).map(([type, count]) => {
                const pill = CASE_FILE_TYPE_PILL[type] || TYPE_PILL_DEFAULT;
                return (
                    <span key={type} style={{ ...caseFileTypePillBase, ...pill, fontSize: '0.75rem', padding: '2px 10px' }}>
                        {type}: {count}
                    </span>
                );
            })}
        </div>
    );
};

const TypePill = ({ type }) => {
    const pill = CASE_FILE_TYPE_PILL[type] || TYPE_PILL_DEFAULT;
    return (
        <span style={{ ...caseFileTypePillBase, ...pill, fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px' }}>
            {type || '—'}
        </span>
    );
};

const NilRow = ({ colSpan }) => (
    <tr>
        <td colSpan={colSpan} style={{ textAlign: 'center', padding: '14px 10px', color: '#9ca3af', fontStyle: 'italic', fontSize: 13 }}>
            NIL RECORDS
        </td>
    </tr>
);

const SectionHeader = ({ title, counts, isDark, isArrival }) => {
    const bg   = isDark ? (isArrival ? '#0c3a5c' : '#4a2d00') : (isArrival ? '#e8f0fe' : '#fef3c7');
    const color = isDark ? (isArrival ? '#93c5fd' : '#fcd34d') : (isArrival ? '#1e40af' : '#92400e');
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: bg, padding: '8px 12px', borderRadius: '4px 4px 0 0',
            marginTop: 20,
        }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color }}>
                {title}
            </span>
            {counts && <TypeCounts counts={counts} />}
        </div>
    );
};

const reportTable = (isDark) => ({
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    background: isDark ? '#1e2433' : '#fff',
});

const thStyle = (isDark) => ({
    padding: '7px 10px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: `2px solid ${isDark ? '#2e3a50' : '#e5e7eb'}`,
    color: isDark ? '#9ca3af' : '#374151',
    background: isDark ? '#252d3d' : '#f8faff',
    whiteSpace: 'nowrap',
});

const tdStyle = (isDark) => ({
    padding: '7px 10px',
    fontSize: 12,
    borderBottom: `1px solid ${isDark ? '#2a3042' : '#f3f4f6'}`,
    color: isDark ? '#d1d5db' : '#1f2937',
    verticalAlign: 'top',
});

const FlightSummary = () => {
    const themeType = useRecoilValue(themeTypeState);
    const isDark = themeType === 'dark';

    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [flightType, setFlightType] = useState('arrival');
    const [flights, setFlights] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedMail, setExpandedMail] = useState(null);

    const [reportData, setReportData] = useState(null);
    const [reportModalOpen, setReportModalOpen] = useState(false);

    const [confirmFlight, setConfirmFlight] = useState(null);
    const [emailingFlight, setEmailingFlight] = useState(null);
    const [downloadingFlight, setDownloadingFlight] = useState(null);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const loadFlights = useCallback(async () => {
        setLoading(true);
        const d = date;
        const leg = flightType;
        const skyFn = leg === 'departure' ? getDepartureFlightListRequest : getArrivalFlightListRequest;
        const [skyopsRes, dataRes] = await Promise.all([
            skyFn({ Date: d }),
            flightListRequest({ Date: d, FlightType: leg }),
        ]);

        const skyopsNames = (skyopsRes?.data || []).map(f => f.name).filter(Boolean);
        const dataFlights = Array.isArray(dataRes?.data) ? dataRes.data : [];
        const dataMap = {};
        dataFlights.forEach(f => { if (f.flight) dataMap[f.flight] = f; });

        const allNames = [...new Set([...skyopsNames, ...dataFlights.map(f => f.flight).filter(Boolean)])].sort();

        setFlights(allNames.map(name => ({
            flight: name,
            originated_count:    dataMap[name]?.originated_count    || 0,
            received_count:      dataMap[name]?.received_count      || 0,
            forwarded_count:     dataMap[name]?.forwarded_count     || 0,
            from_transfer_count: dataMap[name]?.from_transfer_count || 0,
            to_transfer_count:   dataMap[name]?.to_transfer_count   || 0,
            mail_logs:           dataMap[name]?.mail_logs           || [],
        })));
        setLoading(false);
    }, [date, flightType]);

    useEffect(() => { loadFlights(); }, [loadFlights]);

    const handleDateChange = (_, dateStr) => {
        if (!dateStr) return;
        setDate(dateStr);
    };

    const closeModal = () => { setReportData(null); setReportModalOpen(false); };

    const viewFlight = async (f) => {
        const res = await viewFlightRequest({ FlightDate: date, Flight: f.flight, FlightType: flightType });
        if (res?.data) {
            setReportData(res.data);
            setReportModalOpen(true);
        }
    };

    const emailFlight = async () => {
        const flight = confirmFlight;
        setConfirmFlight(null);
        setEmailingFlight(flight);
        const res = await mailFlightRequest({ FlightDate: date, Flight: flight, FlightType: flightType });
        handleResponse(res);
        setEmailingFlight(null);
        if (res?.Status === 'success') loadFlights();
    };

    const downloadFlight = async (flight) => {
        setDownloadingFlight(flight);
        const blob = await exportFlightRequest({ FlightDate: date, Flight: flight, FlightType: flightType });
        setDownloadingFlight(null);
        if (!blob) return;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${flight}-${date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.parentNode.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const downloadPdf = () => {
        setDownloadingPdf(true);
        const printArea = document.getElementById('flight-summary-modal-body');
        html2canvas(printArea).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF();
            pdf.addImage(imgData, 'JPEG', 0, 0);
            pdf.save(`${reportData?.Flight}-${reportData?.RawDate}.pdf`);
            setDownloadingPdf(false);
        });
    };

    const [search, setSearch] = useState('');

    const mutedColor  = isDark ? '#6c757d' : '#adb5bd';
    const subTextColor = isDark ? '#adb5bd' : '#6c757d';

    const visibleFlights = flights.filter(f => f.flight?.toLowerCase().includes(search.toLowerCase()));
    const isArrival = flightType === 'arrival';
    /** Flight list: arrival shows Received only; departure shows Forwarded only (6 data columns incl. Transfer). */
    const summaryTableColSpan = 6;

    const renderReport = () => {
        if (!reportData) return null;
        const rd = reportData;
        const arrival = rd.FlightType === 'arrival';

        return (
            <div>
                {/* Report header */}
                <div style={{
                    background: arrival ? '#0ea5e9' : '#f59e0b',
                    padding: '12px 16px',
                    borderRadius: 4,
                    marginBottom: 16,
                }}>
                    <span style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#fff' }}>
                        {arrival ? 'Arriving' : 'Departing'} Flight Report
                    </span>
                </div>
                <table style={{ width: '100%', fontSize: 13, marginBottom: 16 }}>
                    <tbody>
                        <tr>
                            <td style={{ width: 160, color: subTextColor, fontWeight: 600, padding: '3px 0' }}>FLIGHT REPORT FOR:</td>
                            <td style={{ fontWeight: 700, fontSize: 15 }}>{rd.Flight}</td>
                        </tr>
                        <tr>
                            <td style={{ color: subTextColor, fontWeight: 600, padding: '3px 0' }}>DATE:</td>
                            <td>{rd.Date}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Cases Created */}
                <SectionHeader title="Cases Created" counts={rd.CasesCreatedCounts} isDark={isDark} isArrival={arrival} />
                <table style={reportTable(isDark)}>
                    <thead>
                        <tr>
                            {['Type', 'Reference', 'Address', 'Bags', 'Status'].map(h => (
                                <th key={h} style={thStyle(isDark)}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rd.CasesCreated?.length > 0 ? rd.CasesCreated.map((c, i) => (
                            <tr key={i}>
                                <td style={tdStyle(isDark)}><TypePill type={c.type} /></td>
                                <td style={tdStyle(isDark)}>{c.reference || '—'}</td>
                                <td style={tdStyle(isDark)}>{c.address || '—'}</td>
                                <td style={tdStyle(isDark)}>{c.total_bags ?? '—'}</td>
                                <td style={tdStyle(isDark)}>
                                    <div>{c.case_status || '—'}</div>
                                    {c.type === 'DPR' && c.claim_type && (
                                        <div style={{ fontSize: 11, color: subTextColor, marginTop: 2 }}>{c.claim_type}</div>
                                    )}
                                </td>
                            </tr>
                        )) : <NilRow colSpan={5} />}
                    </tbody>
                </table>

                {arrival ? (
                    <>
                        {/* Cases Received */}
                        <SectionHeader title="Cases Received" counts={rd.CasesReceivedCounts} isDark={isDark} isArrival={arrival} />
                        <table style={reportTable(isDark)}>
                            <thead>
                                <tr>
                                    {['Type', 'Reference', 'Address', 'Total Bags', 'Bags RCVD'].map(h => (
                                        <th key={h} style={thStyle(isDark)}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rd.CasesReceived?.length > 0 ? rd.CasesReceived.map((c, i) => (
                                    <tr key={i}>
                                        <td style={tdStyle(isDark)}><TypePill type={c.type} /></td>
                                        <td style={tdStyle(isDark)}>{c.reference || '—'}</td>
                                        <td style={tdStyle(isDark)}>{c.address || '—'}</td>
                                        <td style={tdStyle(isDark)}>{c.total_bags ?? '—'}</td>
                                        <td style={tdStyle(isDark)}>{c.rcvd_count ?? '—'}</td>
                                    </tr>
                                )) : <NilRow colSpan={5} />}
                            </tbody>
                        </table>

                        {/* Inbound Transfer Bags */}
                        <SectionHeader title="Inbound Transfer Bags" isDark={isDark} isArrival={arrival} />
                        <table style={reportTable(isDark)}>
                            <thead>
                                <tr>
                                    {['To Flight', 'To Flight Date', 'Total Bags', 'Bags Connected'].map(h => (
                                        <th key={h} style={thStyle(isDark)}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rd.InboundTransferBags?.length > 0 ? rd.InboundTransferBags.map((tb, i) => (
                                    <tr key={i}>
                                        <td style={tdStyle(isDark)}>{tb.to_flight || '—'}</td>
                                        <td style={tdStyle(isDark)}>{tb.to_flight_date ? moment(tb.to_flight_date).format('DD-MMM-YY') : '—'}</td>
                                        <td style={tdStyle(isDark)}>{tb.total_bags ?? '—'}</td>
                                        <td style={tdStyle(isDark)}>{tb.bags_connected ?? '—'}</td>
                                    </tr>
                                )) : <NilRow colSpan={4} />}
                            </tbody>
                        </table>
                    </>
                ) : (
                    <>
                        {/* Outbound Transfer Bags */}
                        <SectionHeader title="Outbound Transfer Bags" isDark={isDark} isArrival={arrival} />
                        <table style={reportTable(isDark)}>
                            <thead>
                                <tr>
                                    {['From Flight', 'From Flight Date', 'Total Bags', 'Bags Connected'].map(h => (
                                        <th key={h} style={thStyle(isDark)}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rd.OutboundTransferBags?.length > 0 ? rd.OutboundTransferBags.map((tb, i) => (
                                    <tr key={i}>
                                        <td style={tdStyle(isDark)}>{tb.from_flight || '—'}</td>
                                        <td style={tdStyle(isDark)}>{tb.from_flight_date ? moment(tb.from_flight_date).format('DD-MMM-YY') : '—'}</td>
                                        <td style={tdStyle(isDark)}>{tb.total_bags ?? '—'}</td>
                                        <td style={tdStyle(isDark)}>{tb.bags_connected ?? '—'}</td>
                                    </tr>
                                )) : <NilRow colSpan={4} />}
                            </tbody>
                        </table>

                        {/* Forwarded Cases */}
                        <SectionHeader title="Forwarded" counts={rd.ForwardedCounts} isDark={isDark} isArrival={arrival} />
                        <table style={reportTable(isDark)}>
                            <thead>
                                <tr>
                                    {['Type', 'Reference', 'From Flight', 'Date', 'Bags'].map(h => (
                                        <th key={h} style={thStyle(isDark)}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rd.ForwardedCases?.length > 0 ? rd.ForwardedCases.map((c, i) => (
                                    <tr key={i}>
                                        <td style={tdStyle(isDark)}><TypePill type={c.type} /></td>
                                        <td style={tdStyle(isDark)}>{c.reference || '—'}</td>
                                        <td style={tdStyle(isDark)}>{c.from_flight || '—'}</td>
                                        <td style={tdStyle(isDark)}>{c.date ? moment(c.date).format('DD-MMM-YY') : '—'}</td>
                                        <td style={tdStyle(isDark)}>{c.bags_connected ?? '—'}</td>
                                    </tr>
                                )) : <NilRow colSpan={5} />}
                            </tbody>
                        </table>
                    </>
                )}

                {/* END OF REPORT */}
                <div style={{ textAlign: 'center', padding: '20px 0 8px', color: subTextColor, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    — END OF REPORT —
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <h3 className="mb-0">Flight Summary</h3>
                        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                            <ButtonGroup size="sm">
                                <Button
                                    color="primary"
                                    outline={flightType !== 'arrival'}
                                    onClick={() => setFlightType('arrival')}
                                >
                                    <i className="mdi mdi-airplane-landing me-1" />Arrival
                                </Button>
                                <Button
                                    color="primary"
                                    outline={flightType !== 'departure'}
                                    onClick={() => setFlightType('departure')}
                                >
                                    <i className="mdi mdi-airplane-takeoff me-1" />Departure
                                </Button>
                            </ButtonGroup>
                            <div className="input-group input-group-sm" style={{ width: 190 }}>
                                <span className="input-group-text"><i className="mdi mdi-calendar" /></span>
                                <Flatpickr
                                    value={date}
                                    onChange={handleDateChange}
                                    options={mergeOperationalDateCaps({ dateFormat: 'Y-m-d', altInput: true, altFormat: 'j M Y' })}
                                    className="form-control"
                                />
                            </div>
                        </div>
                    </div>
                    <Card>
                        <CardBody>
                            <div className="mb-3">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search flight..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    style={{ maxWidth: 260 }}
                                />
                            </div>

                            {loading ? (
                                <div className="text-center py-5"><Spinner /></div>
                            ) : visibleFlights.length === 0 ? (
                                <div className="text-center py-5" style={{ color: subTextColor }}>
                                    {flights.length === 0 ? 'No flights found for this date.' : 'No flights match your search.'}
                                </div>
                            ) : (
                                <>
                                    {/* md+ : table */}
                                    <div className="table-responsive d-none d-md-block">
                                        <table className="table table-bordered align-middle mb-0">
                                            <thead style={isDark ? { background: '#2a3042', color: '#adb5bd' } : {}}>
                                                <tr>
                                                    <th>Flight</th>
                                                    <th className="text-center">Originated</th>
                                                    {isArrival ? <th className="text-center">Received</th> : null}
                                                    {!isArrival ? <th className="text-center">Forwarded</th> : null}
                                                    <th className="text-center">Transfer</th>
                                                    <th>Last Emailed</th>
                                                    <th className="text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleFlights.map(f => (
                                                    <React.Fragment key={f.flight}>
                                                        <tr>
                                                            <td className="fw-semibold">{f.flight}</td>
                                                            <td className="text-center">{f.originated_count    > 0 ? f.originated_count    : <span style={{ color: mutedColor }}>—</span>}</td>
                                                            {isArrival ? (
                                                                <td className="text-center">{f.received_count > 0 ? f.received_count : <span style={{ color: mutedColor }}>—</span>}</td>
                                                            ) : null}
                                                            {!isArrival ? (
                                                                <td className="text-center">{f.forwarded_count > 0 ? f.forwarded_count : <span style={{ color: mutedColor }}>—</span>}</td>
                                                            ) : null}
                                                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                                                <div style={{ fontSize: 13 }}>
                                                                    <i className="mdi mdi-airplane-landing me-1" style={{ color: '#0ab39c' }} />
                                                                    {f.from_transfer_count > 0 ? f.from_transfer_count : <span style={{ color: mutedColor }}>—</span>}
                                                                </div>
                                                                <div style={{ fontSize: 13, marginTop: 2 }}>
                                                                    <i className="mdi mdi-airplane-takeoff me-1" style={{ color: '#405189' }} />
                                                                    {f.to_transfer_count > 0 ? f.to_transfer_count : <span style={{ color: mutedColor }}>—</span>}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                {f.mail_logs.length === 0 ? (
                                                                    <span style={{ color: mutedColor, fontSize: 13 }}>Never</span>
                                                                ) : (
                                                                    <div>
                                                                        <div style={{ fontSize: 13 }}>
                                                                            {moment(f.mail_logs[0].created_at).format('DD MMM, HH:mm')}
                                                                            <span style={{ color: subTextColor }}> · {f.mail_logs[0].user?.name}</span>
                                                                        </div>
                                                                        {f.mail_logs.length > 1 && (
                                                                            <button
                                                                                className="btn btn-link btn-sm p-0"
                                                                                style={{ fontSize: 12 }}
                                                                                onClick={() => setExpandedMail(expandedMail === f.flight ? null : f.flight)}
                                                                            >
                                                                                {expandedMail === f.flight ? 'Hide history' : `+${f.mail_logs.length - 1} more`}
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-1 justify-content-center">
                                                                    <button className="btn btn-sm btn-outline-secondary" title="View report" onClick={() => viewFlight(f)}>
                                                                        <i className="mdi mdi-eye" />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-info"
                                                                        title="Send email"
                                                                        disabled={!!emailingFlight}
                                                                        onClick={() => setConfirmFlight(f.flight)}
                                                                    >
                                                                        {emailingFlight === f.flight ? <Spinner size="sm" /> : <i className="mdi mdi-email-outline" />}
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-success"
                                                                        title="Download Excel"
                                                                        disabled={!!downloadingFlight}
                                                                        onClick={() => downloadFlight(f.flight)}
                                                                    >
                                                                        {downloadingFlight === f.flight ? <Spinner size="sm" /> : <i className="mdi mdi-file-excel-outline" />}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {expandedMail === f.flight && (
                                                            <tr>
                                                                <td colSpan={summaryTableColSpan} style={{ padding: 0 }}>
                                                                    <div style={{
                                                                        background: isDark ? '#1e2433' : '#f8f9fa',
                                                                        padding: '10px 16px',
                                                                        borderTop: `1px solid ${isDark ? 'rgba(255,255,255,.08)' : '#dee2e6'}`,
                                                                    }}>
                                                                        <p className="mb-2 fw-semibold" style={{ fontSize: 13 }}>Mail History</p>
                                                                        <ul className="list-unstyled mb-0">
                                                                            {f.mail_logs.map((log, i) => (
                                                                                <li key={i} style={{ fontSize: 13, color: subTextColor }}>
                                                                                    {moment(log.created_at).format('DD MMM YYYY, HH:mm')} — {log.user?.name}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* xs/sm : cards */}
                                    <div className="d-md-none">
                                        {visibleFlights.map(f => (
                                            <div key={f.flight} className="mb-2 rounded" style={{
                                                border: `1px solid ${isDark ? 'rgba(255,255,255,.1)' : '#dee2e6'}`,
                                                background: isDark ? '#2a3042' : '#fff',
                                            }}>
                                                <div className="p-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <span className="fw-bold" style={{ fontSize: 15 }}>{f.flight}</span>
                                                        <div className="d-flex gap-1">
                                                            <button className="btn btn-sm btn-outline-secondary" title="View report" onClick={() => viewFlight(f)}>
                                                                <i className="mdi mdi-eye" />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-info"
                                                                title="Send email"
                                                                disabled={!!emailingFlight}
                                                                onClick={() => setConfirmFlight(f.flight)}
                                                            >
                                                                {emailingFlight === f.flight ? <Spinner size="sm" /> : <i className="mdi mdi-email-outline" />}
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-success"
                                                                title="Download Excel"
                                                                disabled={!!downloadingFlight}
                                                                onClick={() => downloadFlight(f.flight)}
                                                            >
                                                                {downloadingFlight === f.flight ? <Spinner size="sm" /> : <i className="mdi mdi-file-excel-outline" />}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="row g-2 text-center mb-3" style={{ fontSize: 13 }}>
                                                        <div className="col-6">
                                                            <div style={{ color: subTextColor, marginBottom: 2 }}>Originated</div>
                                                            <div className="fw-semibold">{f.originated_count > 0 ? f.originated_count : <span style={{ color: mutedColor }}>—</span>}</div>
                                                        </div>
                                                        {isArrival ? (
                                                            <div className="col-6">
                                                                <div style={{ color: subTextColor, marginBottom: 2 }}>Received</div>
                                                                <div className="fw-semibold">{f.received_count > 0 ? f.received_count : <span style={{ color: mutedColor }}>—</span>}</div>
                                                            </div>
                                                        ) : (
                                                            <div className="col-6">
                                                                <div style={{ color: subTextColor, marginBottom: 2 }}>Forwarded</div>
                                                                <div className="fw-semibold">{f.forwarded_count > 0 ? f.forwarded_count : <span style={{ color: mutedColor }}>—</span>}</div>
                                                            </div>
                                                        )}
                                                        <div className="col-6">
                                                            <div style={{ color: subTextColor, marginBottom: 2 }}>
                                                                <i className="mdi mdi-airplane-landing me-1" style={{ color: '#0ab39c' }} />Trn From
                                                            </div>
                                                            <div className="fw-semibold">{f.from_transfer_count > 0 ? f.from_transfer_count : <span style={{ color: mutedColor }}>—</span>}</div>
                                                        </div>
                                                        <div className="col-6">
                                                            <div style={{ color: subTextColor, marginBottom: 2 }}>
                                                                <i className="mdi mdi-airplane-takeoff me-1" style={{ color: '#405189' }} />Trn To
                                                            </div>
                                                            <div className="fw-semibold">{f.to_transfer_count > 0 ? f.to_transfer_count : <span style={{ color: mutedColor }}>—</span>}</div>
                                                        </div>
                                                    </div>

                                                    <div style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,.08)' : '#dee2e6'}`, paddingTop: 8, fontSize: 13 }}>
                                                        {f.mail_logs.length === 0 ? (
                                                            <span style={{ color: mutedColor }}>Never emailed</span>
                                                        ) : (
                                                            <div>
                                                                <span style={{ color: subTextColor }}>Last emailed: </span>
                                                                {moment(f.mail_logs[0].created_at).format('DD MMM, HH:mm')}
                                                                <span style={{ color: subTextColor }}> · {f.mail_logs[0].user?.name}</span>
                                                                {f.mail_logs.length > 1 && (
                                                                    <button
                                                                        className="btn btn-link btn-sm p-0 ms-2"
                                                                        style={{ fontSize: 12 }}
                                                                        onClick={() => setExpandedMail(expandedMail === f.flight ? null : f.flight)}
                                                                    >
                                                                        {expandedMail === f.flight ? 'Hide history' : `+${f.mail_logs.length - 1} more`}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {expandedMail === f.flight && (
                                                        <div className="rounded mt-2 p-2" style={{ background: isDark ? '#1e2433' : '#f8f9fa', fontSize: 13 }}>
                                                            <p className="mb-1 fw-semibold">Mail History</p>
                                                            <ul className="list-unstyled mb-0">
                                                                {f.mail_logs.map((log, i) => (
                                                                    <li key={i} style={{ color: subTextColor }}>
                                                                        {moment(log.created_at).format('DD MMM YYYY, HH:mm')} — {log.user?.name}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Container>

                {/* Email confirm dialog */}
                <Modal size="sm" isOpen={!!confirmFlight} toggle={() => setConfirmFlight(null)}>
                    <ModalHeader toggle={() => setConfirmFlight(null)}>Send Flight Report</ModalHeader>
                    <ModalBody>
                        Send the {isArrival ? 'arrival' : 'departure'} report email for <strong>{confirmFlight}</strong> on <strong>{date}</strong>?
                    </ModalBody>
                    <ModalFooter>
                        <Button color="info" onClick={emailFlight}>Send</Button>
                        <Button color="secondary" outline onClick={() => setConfirmFlight(null)}>Cancel</Button>
                    </ModalFooter>
                </Modal>

                {/* Report view modal */}
                <Modal size="lg" isOpen={reportModalOpen} toggle={closeModal} keyboard={false} backdrop="static">
                    <div className="modal-header">
                        <h5 className="modal-title mt-0">Flight Report</h5>
                        <button type="button" onClick={closeModal} className="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body" style={{ padding: 0 }}>
                        <div id="flight-summary-modal-body" style={{ overflowX: 'auto', padding: '1rem' }}>
                            {renderReport()}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" onClick={closeModal} className="btn btn-secondary waves-effect">Close</button>
                        <button type="button" onClick={downloadPdf} className="btn btn-primary waves-effect" disabled={downloadingPdf}>
                            {downloadingPdf ? <Spinner size="sm" /> : <><i className="mdi mdi-file-pdf-box me-1" />Download PDF</>}
                        </button>
                    </div>
                </Modal>
            </div>
        </React.Fragment>
    );
};

export default FlightSummary;
